import { type InstalledWidget, type Widget } from "@repo/api";

import { APPOINTMENTS_SETTINGS } from "./appointmentWidgetSettings";
import { CALENDAR_SETTINGS } from "./calendarWidgetSettings";
import { INSTALLATION_QUOTATIONS_SETTINGS } from "./installationQuotationWidgetSettings";
import { PRECALL_SETTINGS } from "./preCallWidgetSettings";
import { PROJECTS_SETTINGS } from "./projectsWidgetSettings";
import { QUOTATIONS_SETTINGS } from "./quotationWidgetSettings";
import { TASKS_SETTINGS } from "./tasksWidgetSettings";
import { WAITING_LIST_SETTINGS } from "./waitingListWidgetSettings";
import { WORK_ORDERS_SETTINGS } from "./workOrderWidgetSettings";

export interface WidgetSettingsProps {
  hasTags: boolean;
  isPromotionalConsentEnabled: boolean;
  isColdProjectEnabled: boolean;
  isKICEnabled: boolean;
  isProjectStagesEnabled: boolean;
  projectStageValues?: string[];
  supportedLanguages?: string[];
  bokaServices?: string[];
  isGeneratedProjectsEnabled?: boolean;
  isSalesForceEnabled?: boolean;
  orderMethods?: string[];
  isWaitingListEnabled?: boolean;
}

export const isInstalledWidget = (
  widgetInfo?: Widget | InstalledWidget
): widgetInfo is InstalledWidget =>
  widgetInfo ? Object.hasOwn(widgetInfo, "workspacePreferenceId") : false;

export const WIDGETS = ({
  hasTags,
  supportedLanguages,
  bokaServices,
  orderMethods,
  isPromotionalConsentEnabled,
  isColdProjectEnabled,
  isGeneratedProjectsEnabled,
  isKICEnabled,
  isSalesForceEnabled,
  isProjectStagesEnabled,
  projectStageValues,
  isWaitingListEnabled,
}: WidgetSettingsProps): Widget[] =>
  [
    {
      widgetType: "TASKS",
      name: "label.tasks",
      category: "Sales lead",
      sizeDefinition: [
        { size: "S", col: 1, row: 1 },
        { size: "M", col: 1, row: 2 },
      ],
      settingsDefinition: TASKS_SETTINGS,
    },
    {
      widgetType: "ALERTS",
      name: "label.project_notifications",
      category: "Sales lead",
      sizeDefinition: [
        { size: "M", col: 2, row: 1 },
        { size: "L", col: 4, row: 1 },
      ],
    },
    {
      widgetType: "CALENDAR",
      name: "label.calendar_title",
      category: "Sales lead",
      sizeDefinition: [
        { size: "S", col: 1, row: 1 },
        { size: "M", col: 1, row: 2 },
      ],
      settingsDefinition: CALENDAR_SETTINGS,
    },
    {
      widgetType: "PROJECTS",
      name: "label.projects",
      category: "Sales lead",
      sizeDefinition: [
        { size: "M", col: 2, row: 1 },
        { size: "L", col: 4, row: 1 },
      ],
      settingsDefinition: PROJECTS_SETTINGS({
        hasTags,
        supportedLanguages,
        bokaServices,
        orderMethods,
        isPromotionalConsentEnabled,
        isColdProjectEnabled,
        isKICEnabled,
        isSalesForceEnabled,
        isProjectStagesEnabled,
        projectStageValues,
      }),
    },
    {
      widgetType: "PRECALL",
      name: "label.precall",
      category: "Sales lead",
      sizeDefinition: [
        { size: "S", col: 1, row: 1 },
        { size: "M", col: 1, row: 2 },
      ],
      settingsDefinition: PRECALL_SETTINGS,
    },
    {
      widgetType: "QUOTATIONS",
      name: "label.quotations",
      category: "Sales lead",
      sizeDefinition: [
        { size: "S", col: 1, row: 1 },
        { size: "M", col: 1, row: 2 },
      ],
      settingsDefinition: QUOTATIONS_SETTINGS,
    },
    {
      widgetType: "APPOINTMENTS",
      name: "label.appointments",
      category: "Sales lead",
      sizeDefinition: [
        { size: "M", col: 2, row: 1 },
        { size: "L", col: 4, row: 1 },
      ],
      settingsDefinition: APPOINTMENTS_SETTINGS,
    },
    {
      widgetType: "INSTALLATION_QUOTATIONS",
      name: "label.installation_quotations",
      category: "Sales lead",
      sizeDefinition: [
        { size: "S", col: 1, row: 1 },
        { size: "M", col: 1, row: 2 },
      ],
      settingsDefinition: INSTALLATION_QUOTATIONS_SETTINGS,
    },

    {
      widgetType: "WORK_ORDERS",
      name: "label.work_orders",
      category: "Sales lead",
      sizeDefinition: [
        { size: "S", col: 1, row: 1 },
        { size: "M", col: 1, row: 2 },
      ],
      settingsDefinition: WORK_ORDERS_SETTINGS,
    },
    isGeneratedProjectsEnabled
      ? {
          widgetType: "FOLLOWUP_LIST",
          name: "label.follow_up_list",
          category: "Sales lead",
          sizeDefinition: [{ size: "L", col: 4, row: 1 }],
        }
      : undefined,
    isWaitingListEnabled
      ? {
          widgetType: "WAITING_LIST",
          name: "label.waiting_list",
          category: "Sales lead",
          sizeDefinition: [
            { size: "S", col: 1, row: 1 },
            { size: "M", col: 1, row: 2 },
          ],
          settingsDefinition: WAITING_LIST_SETTINGS,
        }
      : undefined,
  ].filter((widget): widget is Widget => widget !== undefined);
